/**
 * Booking Context - Manages booking state across steps
 * Updated: 6 étapes (véhicule intégré à l'étape trajet)
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

const BookingContext = createContext(null);

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};

const TOTAL_STEPS = 5; // Trajet(+véhicule) → Passagers → Tarif → Identité → Validation(+paiement)

const initialBookingState = {
  // Type de service
  service_type: 'transfer', // 'transfer' ou 'hourly'
  
  // Step 1 - Trajet + Véhicule
  pickup: null,
  dropoff: null,
  intermediate_stops: [],
  pickup_datetime: '',
  is_round_trip: false,
  return_datetime: '',
  vehicle_category: 'berline',
  // Détection aéroport/gare
  flight_number: '',
  train_number: '',
  pickup_zone: null,
  dropoff_zone: null,
  pricing_method: null,
  
  // Mise à disposition (hourly)
  hours_booked: null,
  
  // Step 2 - Passagers et options
  passengers: 1,
  luggage: 0,
  passenger_names: [],
  options: [],
  special_requests: '',
  
  // Step 3 - Tarif (calculé)
  price_estimate: null,
  price_details: null,
  
  // Step 4 - Identité client
  client_email: '',
  client_name: '',
  client_phone: '',
  
  // Step 5 - Validation
  cgv_accepted: false,
  
  // Résultat réservation
  booking_id: null,
  booking_status: null,
  
  // Step 6 - Paiement
  stripe_session_id: null,
  payment_status: null,
};

export const BookingProvider = ({ children }) => {
  const [bookingData, setBookingData] = useState(initialBookingState);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Update booking data
  const updateBookingData = useCallback((updates) => {
    setBookingData(prev => ({ ...prev, ...updates }));
  }, []);

  // Reset booking
  const resetBooking = useCallback(() => {
    setBookingData(initialBookingState);
    setCurrentStep(1);
    setErrors({});
  }, []);

  // Navigation
  const nextStep = useCallback(() => {
    setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }, []);

  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= TOTAL_STEPS) {
      setCurrentStep(step);
    }
  }, []);

  // Validation helpers
  const setStepErrors = useCallback((stepErrors) => {
    setErrors(prev => ({ ...prev, ...stepErrors }));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const value = {
    bookingData,
    updateBookingData,
    resetBooking,
    currentStep,
    setCurrentStep,
    nextStep,
    prevStep,
    goToStep,
    errors,
    setStepErrors,
    clearErrors,
    isLoading,
    setIsLoading,
    totalSteps: TOTAL_STEPS,
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export default BookingContext;
